import { enqueueCompanyInvestigation } from './companyInvestigation';
import {
  enabledTemplatesForType,
  mockNextRunAt,
  mockPublicId,
  nextScheduleId,
} from './scheduleWatchAgents';
import { createDraftWatchCompany, formatAgentType, nextWatchCompanyId } from './watchTowerSelectors';

import type {
  WatchCompany,
  WatchFinding,
  WatchFindingAffected,
  WatchFindingSourceKind,
  WatchRun,
  WatchSchedule,
  WatchTopic,
  WatchTopicMatchKind,
  WatchTopicMatchRules,
  WatchTowerMockState,
} from './types';

export const TOPIC_MATCH_KINDS: WatchTopicMatchKind[] = [
  'identity_geo',
  'industry',
  'product',
  'building',
  'org',
  'association',
  'financials',
];

export const TOPIC_ELEVATED_SEVERITIES = new Set(['medium', 'high', 'critical']);

const FINDING_TYPE_BY_AGENT: Record<string, string> = {
  'regulation-monitoring': 'regulation_signal',
  'climate-monitoring': 'climate_finding',
  'reputation-monitoring': 'media_article',
  'finance-monitoring': 'financial_finding',
  'ownership-monitoring': 'ownership_finding',
};

export function slugTopicUid(title: string, id: number): string {
  const slug = title
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40);
  return `WT-TOPIC-${slug || id}`;
}

export function seedWatchTopics(createdAt = '2026-08-20T08:00:00.000Z'): WatchTopic[] {
  return [
    {
      id: 1,
      uid: 'WT-TOPIC-EU-FOOD-CONTACT',
      title: 'EU food-contact plastics',
      briefMarkdown:
        'Watch EU draft restrictions on food-contact PET and related resins.\n'
        + 'Match converters and resin makers on HS 3907 and NACE C.',
      agentType: 'regulation-monitoring',
      subType: 'regulation-tracking',
      agentConfigId: 5,
      templateName: 'Product Regulation default',
      matchKinds: ['product', 'industry'],
      matchRulesJson: {
        naceSections: ['C'],
        hsPrefixes: ['3907'],
      },
      status: 'active',
      paused: false,
      createdAt,
      lastRunAt: null,
      briefChatId: null,
      namedMisses: [
        {
          name: 'Nordic Tray Converters Oy',
          uid: 'WT-TOPIC-MISS-NORDIC-TRAY',
          addressCountry: 'FI',
          addressCity: 'Tampere',
          notes: 'Named converter. Not in the registry.',
          importedWatchCompanyId: null,
        },
      ],
    },
    {
      id: 2,
      uid: 'WT-TOPIC-ARMED-CONFLICT-UA',
      title: 'Armed conflict in Ukraine',
      briefMarkdown:
        'Watch hostilities and related ownership or sentiment signals in Ukraine.\n'
        + 'Match seat, site, or association events in UA.',
      agentType: 'reputation-monitoring',
      subType: 'media-sentiment',
      agentConfigId: 7,
      templateName: 'Public Sentiment default',
      matchKinds: ['identity_geo', 'building', 'association'],
      matchRulesJson: {
        anyKind: true,
        countries: ['UA'],
      },
      status: 'active',
      paused: false,
      createdAt,
      lastRunAt: null,
      briefChatId: null,
      namedMisses: [
        {
          name: 'Steppe Grain Export LLC',
          uid: 'WT-TOPIC-MISS-STEPPE-GRAIN',
          addressCountry: 'UA',
          addressCity: 'Odesa',
          notes: 'Named exporter. Not in the registry.',
          importedWatchCompanyId: null,
        },
      ],
    },
    {
      id: 3,
      uid: 'WT-TOPIC-VALENCIA-DROUGHT-2026',
      title: 'Valencia drought 2026',
      briefMarkdown:
        'Watch water restrictions in the Valencia basin.\n'
        + 'Match firms with a seat or mill there and citrus or extract products.',
      agentType: 'climate-monitoring',
      subType: 'climate-risk',
      agentConfigId: 6,
      templateName: 'Climate default',
      matchKinds: ['building', 'identity_geo', 'product'],
      matchRulesJson: {
        countries: ['ES'],
        cities: ['Valencia'],
        productNeedles: ['orange', 'citrus', 'oil', 'pectin'],
        hsPrefixes: ['3301'],
      },
      status: 'active',
      paused: false,
      createdAt,
      lastRunAt: null,
      briefChatId: null,
      namedMisses: [
        {
          name: 'Ribera Citrus Coop',
          uid: 'WT-TOPIC-MISS-RIBERA-CITRUS',
          addressCountry: 'ES',
          addressCity: 'Alzira',
          notes: 'Named farm. Not in the registry.',
          importedWatchCompanyId: null,
        },
      ],
    },
    {
      id: 4,
      uid: 'WT-TOPIC-HORMUZ-SHIPPING',
      title: 'Strait of Hormuz shipping risk',
      briefMarkdown:
        'Watch tanker transit, insurance, and security signals at the Strait of Hormuz.\n'
        + 'Match seats or sites in AE, IR, OM, or QA.',
      agentType: 'ownership-monitoring',
      subType: 'industrial-forecasting',
      agentConfigId: 8,
      templateName: 'Ownership default',
      matchKinds: ['identity_geo', 'association'],
      matchRulesJson: {
        anyKind: true,
        countries: ['AE', 'IR', 'OM', 'QA'],
      },
      status: 'active',
      paused: false,
      createdAt,
      lastRunAt: null,
      briefChatId: null,
      namedMisses: [
        {
          name: 'Fujairah Bunker Services LLC',
          uid: 'WT-TOPIC-MISS-FUJAIRAH-BUNKER',
          addressCountry: 'AE',
          addressCity: 'Fujairah',
          notes: 'Named bunker supplier. Not in the registry.',
          importedWatchCompanyId: null,
        },
      ],
    },
    {
      id: 5,
      uid: 'WT-TOPIC-US-TAXATION',
      title: 'US corporate tax and tariff shift',
      briefMarkdown:
        'Watch US federal tax, tariff, and transfer-pricing moves that hit manufacturers.\n'
        + 'Match firms with a US seat.',
      agentType: 'finance-monitoring',
      subType: 'financial-risk',
      agentConfigId: 4,
      templateName: 'Financial Stability default',
      matchKinds: ['identity_geo'],
      matchRulesJson: {
        countries: ['US'],
      },
      status: 'active',
      paused: false,
      createdAt,
      lastRunAt: null,
      briefChatId: null,
      namedMisses: [
        {
          name: 'Harbor Tax Holdings LLC',
          uid: 'WT-TOPIC-MISS-HARBOR-TAX',
          addressCountry: 'US',
          addressCity: 'Wilmington',
          notes: 'Named holding company. Not in the registry.',
          importedWatchCompanyId: null,
        },
      ],
    },
    {
      id: 6,
      uid: 'WT-TOPIC-SOUTHERN-AFRICA-DROUGHT',
      title: 'Southern Africa drought 2026',
      briefMarkdown:
        'Watch water cuts and crop stress in Southern Africa.\n'
        + 'Match firms with a seat or plant in ZA or KE.',
      agentType: 'climate-monitoring',
      subType: 'climate-risk',
      agentConfigId: 6,
      templateName: 'Climate default',
      matchKinds: ['identity_geo', 'building'],
      matchRulesJson: {
        anyKind: true,
        countries: ['ZA', 'KE'],
      },
      status: 'active',
      paused: false,
      createdAt,
      lastRunAt: null,
      briefChatId: null,
      namedMisses: [
        {
          name: 'Karoo Water Logistics Pty',
          uid: 'WT-TOPIC-MISS-KAROO-WATER',
          addressCountry: 'ZA',
          addressCity: 'Beaufort West',
          notes: 'Named water hauler. Not in the registry.',
          importedWatchCompanyId: null,
        },
      ],
    },
    {
      id: 7,
      uid: 'WT-TOPIC-BR-POLITICS',
      title: 'Brazilian politics scandals',
      briefMarkdown:
        'Watch procurement probes and political-risk coverage in Brazil.\n'
        + 'Match firms with a Brazilian seat or plant.',
      agentType: 'reputation-monitoring',
      subType: 'media-sentiment',
      agentConfigId: 7,
      templateName: 'Public Sentiment default',
      matchKinds: ['identity_geo'],
      matchRulesJson: {
        countries: ['BR'],
      },
      status: 'active',
      paused: false,
      createdAt,
      lastRunAt: null,
      briefChatId: null,
      namedMisses: [
        {
          name: 'Planalto Contractors Ltda',
          uid: 'WT-TOPIC-MISS-PLANALTO',
          addressCountry: 'BR',
          addressCity: 'Brasilia',
          notes: 'Named contractor. Not in the registry.',
          importedWatchCompanyId: null,
        },
      ],
    },
    {
      id: 8,
      uid: 'WT-TOPIC-NEPAL-FLOODING',
      title: 'Nepal monsoon flooding',
      briefMarkdown:
        'Watch monsoon floods on roads, plants, and hydropower in Nepal.\n'
        + 'Match seats or sites in NP.',
      agentType: 'climate-monitoring',
      subType: 'climate-risk',
      agentConfigId: 6,
      templateName: 'Climate default',
      matchKinds: ['identity_geo', 'building'],
      matchRulesJson: {
        anyKind: true,
        countries: ['NP'],
      },
      status: 'active',
      paused: false,
      createdAt,
      lastRunAt: null,
      briefChatId: null,
      namedMisses: [
        {
          name: 'Koshi Valley Textiles Pvt',
          uid: 'WT-TOPIC-MISS-KOSHI-TEXTILES',
          addressCountry: 'NP',
          addressCity: 'Biratnagar',
          notes: 'Named mill. Not in the registry.',
          importedWatchCompanyId: null,
        },
      ],
    },
    {
      id: 9,
      uid: 'WT-TOPIC-EU-IMMIGRATION-LAW',
      title: 'EU immigration law reform',
      briefMarkdown:
        'Watch EU labour-permit and asylum-work rules that change plant staffing.\n'
        + 'Match firms with a seat in DE, FR, NL, or BE.',
      agentType: 'regulation-monitoring',
      subType: 'regulation-tracking',
      agentConfigId: 15,
      templateName: 'Product Regulation EU pack',
      matchKinds: ['identity_geo'],
      matchRulesJson: {
        countries: ['DE', 'FR', 'NL', 'BE'],
      },
      status: 'active',
      paused: false,
      createdAt,
      lastRunAt: null,
      briefChatId: null,
      namedMisses: [
        {
          name: 'Meuse Staffing BV',
          uid: 'WT-TOPIC-MISS-MEUSE-STAFF',
          addressCountry: 'BE',
          addressCity: 'Liege',
          notes: 'Named labour broker. Not in the registry.',
          importedWatchCompanyId: null,
        },
      ],
    },
  ];
}

function topicScheduleTimezone(topic: WatchTopic): string {
  if (topic.uid === 'WT-TOPIC-HORMUZ-SHIPPING') {
    return 'Asia/Dubai';
  }
  if (topic.uid === 'WT-TOPIC-US-TAXATION') {
    return 'America/New_York';
  }
  if (topic.uid === 'WT-TOPIC-SOUTHERN-AFRICA-DROUGHT') {
    return 'Africa/Johannesburg';
  }
  if (topic.uid === 'WT-TOPIC-BR-POLITICS') {
    return 'America/Sao_Paulo';
  }
  if (topic.uid === 'WT-TOPIC-NEPAL-FLOODING') {
    return 'Asia/Kathmandu';
  }
  if (topic.uid === 'WT-TOPIC-EU-IMMIGRATION-LAW') {
    return 'Europe/Brussels';
  }
  if (topic.agentType === 'climate-monitoring') {
    return 'Europe/Madrid';
  }
  return 'Europe/Berlin';
}

export function seedTopicSchedules(
  topics: WatchTopic[],
  startId: number,
  now = new Date('2026-09-02T12:00:00.000Z'),
): WatchSchedule[] {
  return topics.map((topic, index) => ({
    id: startId + index,
    title: topic.title,
    watchCompanyId: 0,
    watchTopicId: topic.id,
    type: topic.agentType,
    subType: topic.subType,
    agentConfigId: topic.agentConfigId,
    templateName: topic.templateName,
    timezone: topicScheduleTimezone(topic),
    hourOfDay: 6,
    frequency: 'daily',
    paused: topic.paused,
    nextRunAt: topic.paused ? null : mockNextRunAt(6, 'daily', now),
    lastRunStatus: null,
    startDate: '2026-08-20',
    endDate: null,
    targetKind: 'topic',
    targetIds: [topic.id],
  }));
}

function normalizeList(value: string[] | undefined): string[] {
  return (value || []).map((item) => item.trim()).filter(Boolean);
}

function countryOf(value: string | null | undefined): string {
  return (value || '').trim().toUpperCase();
}

function cityOf(value: string | null | undefined): string {
  return (value || '').trim().toLowerCase();
}

function textHaystack(parts: (string | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ').toLowerCase();
}

function hsDigits(value: string | null | undefined): string {
  return (value || '').replace(/\D/g, '');
}

function matchesCountryList(value: string | null | undefined, countries: string[]): boolean {
  if (!countries.length) {
    return false;
  }
  const code = countryOf(value);
  return countries.some((item) => countryOf(item) === code);
}

function matchesCityList(value: string | null | undefined, cities: string[]): boolean {
  if (!cities.length) {
    return false;
  }
  const city = cityOf(value);
  return cities.some((item) => cityOf(item) === city);
}

function kindHitsIdentityGeo(company: WatchCompany, rules: WatchTopicMatchRules): boolean {
  const countries = normalizeList(rules.countries).map(countryOf);
  const cities = normalizeList(rules.cities).map(cityOf);
  const countryHit = matchesCountryList(company.addressCountry, countries);
  const cityHit = matchesCityList(company.addressCity, cities);
  if (countries.length && cities.length) {
    return countryHit && cityHit;
  }
  if (countries.length) {
    return countryHit;
  }
  if (cities.length) {
    return cityHit;
  }
  return Boolean(company.addressCountry);
}

function kindHitsIndustry(company: WatchCompany, rules: WatchTopicMatchRules): boolean {
  const sections = normalizeList(rules.naceSections).map((item) => item.toUpperCase());
  if (!sections.length) {
    return Boolean(company.naceSection);
  }
  return sections.includes((company.naceSection || '').toUpperCase());
}

function kindHitsProduct(company: WatchCompany, rules: WatchTopicMatchRules): boolean {
  const prefixes = normalizeList(rules.hsPrefixes).map(hsDigits).filter(Boolean);
  const needles = normalizeList(rules.productNeedles).map((item) => item.toLowerCase());
  if (!company.products.length) {
    return false;
  }
  if (!prefixes.length && !needles.length) {
    return true;
  }
  return company.products.some((product) => {
    const digits = hsDigits(product.hsCode);
    const prefixHit = prefixes.some((prefix) => digits.startsWith(prefix));
    const hay = textHaystack([product.name, product.productCategory, product.productType, product.description]);
    const needleHit = needles.some((needle) => hay.includes(needle));
    if (prefixes.length && needles.length) {
      return prefixHit || needleHit;
    }
    if (prefixes.length) {
      return prefixHit;
    }
    return needleHit;
  });
}

function kindHitsBuilding(company: WatchCompany, rules: WatchTopicMatchRules): boolean {
  const countries = normalizeList(rules.countries).map(countryOf);
  const cities = normalizeList(rules.cities).map(cityOf);
  if (!company.buildings.length) {
    return false;
  }
  return company.buildings.some((building) => {
    const countryHit = countries.length
      ? matchesCountryList(building.addressCountry, countries)
      : true;
    const cityHit = cities.length
      ? matchesCityList(building.addressCity, cities)
      : true;
    if (!countries.length && !cities.length) {
      return true;
    }
    return countryHit && cityHit;
  });
}

function kindHitsOrg(company: WatchCompany): boolean {
  return company.orgNodes.length > 0;
}

function kindHitsAssociation(company: WatchCompany, rules: WatchTopicMatchRules): boolean {
  const countries = normalizeList(rules.countries).map(countryOf);
  const eventKinds = normalizeList(rules.eventKinds).map((item) => item.toLowerCase());
  if (!company.associations.length) {
    return false;
  }
  return company.associations.some((row) => {
    const kind = (row.eventKind || '').toLowerCase();
    const kindHit = eventKinds.length ? eventKinds.includes(kind) : true;
    const payloadCountry = typeof row.payloadJson?.country === 'string'
      ? row.payloadJson.country
      : '';
    const countryHit = countries.length
      ? matchesCountryList(payloadCountry, countries)
        || matchesCountryList(row.personNationality, countries)
        || (row.notes || '').toUpperCase().includes(countries[0] || '')
      : true;
    return kindHit && countryHit;
  });
}

function kindHitsFinancials(company: WatchCompany): boolean {
  return company.financials.length > 0;
}

export function topicKindHits(
  company: WatchCompany,
  kind: WatchTopicMatchKind,
  rules: WatchTopicMatchRules,
): boolean {
  if (kind === 'identity_geo') {
    return kindHitsIdentityGeo(company, rules);
  }
  if (kind === 'industry') {
    return kindHitsIndustry(company, rules);
  }
  if (kind === 'product') {
    return kindHitsProduct(company, rules);
  }
  if (kind === 'building') {
    return kindHitsBuilding(company, rules);
  }
  if (kind === 'org') {
    return kindHitsOrg(company);
  }
  if (kind === 'association') {
    return kindHitsAssociation(company, rules);
  }
  return kindHitsFinancials(company);
}

export function companyMatchesTopic(company: WatchCompany, topic: WatchTopic): boolean {
  if (company.status === 'archived') {
    return false;
  }
  const kinds = topic.matchKinds.length ? topic.matchKinds : TOPIC_MATCH_KINDS;
  const rules = topic.matchRulesJson || {};
  const hits = kinds.map((kind) => topicKindHits(company, kind, rules));
  if (rules.anyKind) {
    return hits.some(Boolean);
  }
  return hits.every(Boolean);
}

export function matchWatchCompaniesForTopic(
  state: WatchTowerMockState,
  topic: WatchTopic,
): WatchCompany[] {
  return state.watchCompanies.filter((company) => companyMatchesTopic(company, topic));
}

export function topicById(state: WatchTowerMockState, id: number): WatchTopic | null {
  return (state.watchTopics || []).find((item) => item.id === id) || null;
}

export function scheduleForTopic(state: WatchTowerMockState, topicId: number): WatchSchedule | null {
  return state.schedules.find((item) => item.watchTopicId === topicId) || null;
}

export function findingSourceKind(finding: WatchFinding): WatchFindingSourceKind {
  if (finding.sourceKind) {
    return finding.sourceKind;
  }
  return finding.watchTopicId ? 'topic' : 'company';
}

export function findingSourceLabel(state: WatchTowerMockState, finding: WatchFinding): string {
  if (findingSourceKind(finding) === 'topic') {
    const topic = topicById(state, finding.watchTopicId || 0);
    return topic?.title || 'Topic';
  }
  const company = state.watchCompanies.find((item) => item.id === finding.watchCompanyId);
  return company?.name || String(finding.watchCompanyId || '—');
}

export function topicOpenFindingCount(state: WatchTowerMockState, topicId: number): number {
  return state.findings.filter((finding) => (
    finding.watchTopicId === topicId
    && TOPIC_ELEVATED_SEVERITIES.has(finding.severity)
  )).length;
}

export function topicLinkedCompanyCount(state: WatchTowerMockState, topicId: number): number {
  const findingIds = new Set(
    state.findings.filter((finding) => finding.watchTopicId === topicId).map((item) => item.id),
  );
  const companies = new Set(
    state.findingAffectedCompanies
      .filter((link) => findingIds.has(link.findingId) && link.flaggedBy === 'topic_match')
      .map((link) => link.affectedWatchCompanyId),
  );
  return companies.size;
}

export function topicLastRunAt(state: WatchTowerMockState, topicId: number): string | null {
  const stamps = state.runs
    .filter((run) => run.watchTopicId === topicId)
    .map((run) => run.scheduledAt)
    .sort();
  return stamps.length ? stamps[stamps.length - 1] : null;
}

export function findingsForTopic(state: WatchTowerMockState, topicId: number): WatchFinding[] {
  return state.findings
    .filter((finding) => finding.watchTopicId === topicId)
    .sort((left, right) => right.observedAt.localeCompare(left.observedAt));
}

export function linkedCompaniesForTopicFinding(
  state: WatchTowerMockState,
  findingId: number,
): WatchCompany[] {
  const ids = new Set(
    state.findingAffectedCompanies
      .filter((link) => link.findingId === findingId)
      .map((link) => link.affectedWatchCompanyId),
  );
  return state.watchCompanies.filter((company) => ids.has(company.id));
}

export interface CreateWatchTopicInput {
  title: string,
  type: string,
  subType: string,
  agentConfigId: number,
  briefMarkdown: string,
  matchKinds: WatchTopicMatchKind[],
  matchRulesJson: WatchTopicMatchRules,
  timezone: string,
  hourOfDay: number,
  frequency: string,
  paused: boolean,
  startDate: string,
  endDate: string | null,
  now?: Date,
}

export type CreateWatchTopicError =
  | 'no_title'
  | 'no_template'
  | 'no_brief'
  | 'no_match_kinds'
  | 'no_start_date'
  | 'end_before_start';

export type CreateWatchTopicResult =
  | { ok: true, state: WatchTowerMockState, topicId: number }
  | { ok: false, error: CreateWatchTopicError };

function nextTopicId(state: WatchTowerMockState): number {
  return (state.watchTopics || []).reduce((max, item) => Math.max(max, item.id), 0) + 1;
}

function nextRunId(state: WatchTowerMockState, extra: WatchRun[] = []): number {
  return [...state.runs, ...extra].reduce((max, item) => Math.max(max, item.id), 0) + 1;
}

function nextFindingId(state: WatchTowerMockState, extra: WatchFinding[] = []): number {
  return [...state.findings, ...extra].reduce((max, item) => Math.max(max, item.id), 0) + 1;
}

function nextAffectedId(state: WatchTowerMockState, extra: WatchFindingAffected[] = []): number {
  return [...state.findingAffectedCompanies, ...extra]
    .reduce((max, item) => Math.max(max, item.id), 0) + 1;
}

export function topicScheduleWindowError(
  startDate: string,
  endDate: string | null,
): Extract<CreateWatchTopicError, 'no_start_date' | 'end_before_start'> | null {
  const start = startDate.trim();
  const end = endDate?.trim() || null;
  if (!start) {
    return 'no_start_date';
  }
  if (end && end < start) {
    return 'end_before_start';
  }
  return null;
}

export function createWatchTopic(
  state: WatchTowerMockState,
  input: CreateWatchTopicInput,
): CreateWatchTopicResult {
  const title = input.title.trim();
  if (!title) {
    return { ok: false, error: 'no_title' };
  }
  const template = enabledTemplatesForType(input.type).find((item) => item.id === input.agentConfigId);
  if (!template) {
    return { ok: false, error: 'no_template' };
  }
  if (!input.briefMarkdown.trim()) {
    return { ok: false, error: 'no_brief' };
  }
  if (!input.matchKinds.length) {
    return { ok: false, error: 'no_match_kinds' };
  }
  const windowError = topicScheduleWindowError(input.startDate, input.endDate);
  if (windowError) {
    return { ok: false, error: windowError };
  }
  const now = input.now || new Date();
  const topicId = nextTopicId(state);
  const topic: WatchTopic = {
    id: topicId,
    uid: slugTopicUid(title, topicId),
    title,
    briefMarkdown: input.briefMarkdown.trim(),
    agentType: input.type,
    subType: input.subType,
    agentConfigId: template.id,
    templateName: template.name,
    matchKinds: input.matchKinds,
    matchRulesJson: input.matchRulesJson || {},
    status: 'active',
    paused: input.paused,
    createdAt: now.toISOString(),
    lastRunAt: null,
    namedMisses: [],
    briefChatId: null,
  };
  const schedule: WatchSchedule = {
    id: nextScheduleId(state),
    title,
    watchCompanyId: 0,
    watchTopicId: topicId,
    type: input.type,
    subType: input.subType,
    agentConfigId: template.id,
    templateName: template.name,
    timezone: input.timezone,
    hourOfDay: input.hourOfDay,
    frequency: input.frequency,
    paused: input.paused,
    nextRunAt: input.paused ? null : mockNextRunAt(input.hourOfDay, input.frequency, now),
    lastRunStatus: null,
    startDate: input.startDate.trim(),
    endDate: input.endDate?.trim() || null,
    targetKind: 'topic',
    targetIds: [topicId],
  };
  return {
    ok: true,
    topicId,
    state: {
      ...state,
      watchTopics: [...(state.watchTopics || []), topic],
      schedules: [...state.schedules, schedule],
    },
  };
}

interface CannedTopicFinding {
  severity: string,
  title: string,
  description: string,
  country: string,
  city: string,
  coordinates: string,
  sourceUrl: string | null,
}

const CANNED_TOPIC_FINDINGS: Record<string, CannedTopicFinding> = {
  'WT-TOPIC-EU-FOOD-CONTACT': {
    severity: 'high',
    title: 'Draft EU restriction on food-contact PET resins',
    description: 'Mock topic run. Elevated regulation signal for food-contact plastics.',
    country: 'BE',
    city: 'Brussels',
    coordinates: '50.8503,4.3517',
    sourceUrl: 'https://eur-lex.example/consult/food-contact-2026',
  },
  'WT-TOPIC-ARMED-CONFLICT-UA': {
    severity: 'critical',
    title: 'Hostilities reported near Ukrainian industrial corridors',
    description: 'Mock topic run. Critical sentiment signal for armed conflict.',
    country: 'UA',
    city: 'Kyiv',
    coordinates: '50.4501,30.5234',
    sourceUrl: 'https://news.example/ua-corridor-risk',
  },
  'WT-TOPIC-VALENCIA-DROUGHT-2026': {
    severity: 'critical',
    title: 'Valencia basin water restriction',
    description: 'Mock topic run. Critical climate signal for drought and mill operations.',
    country: 'ES',
    city: 'Valencia',
    coordinates: '39.4699,-0.3763',
    sourceUrl: 'https://aemet.example/drought/valencia',
  },
  'WT-TOPIC-HORMUZ-SHIPPING': {
    severity: 'critical',
    title: 'Tanker transit delays at the Strait of Hormuz',
    description: 'Mock topic run. War-risk premia and chokepoint delays for Gulf crude and condensate.',
    country: 'AE',
    city: 'Fujairah',
    coordinates: '25.1288,56.3265',
    sourceUrl: 'https://news.example/hormuz-transit-2026',
  },
  'WT-TOPIC-US-TAXATION': {
    severity: 'high',
    title: 'US corporate minimum tax draft hits imported APIs',
    description: 'Mock topic run. Finance signal for US manufacturers and inbound pharma inputs.',
    country: 'US',
    city: 'Boston',
    coordinates: '42.3601,-71.0589',
    sourceUrl: 'https://treasury.example/tax/2026-camt',
  },
  'WT-TOPIC-SOUTHERN-AFRICA-DROUGHT': {
    severity: 'critical',
    title: 'Industrial water cuts across the Cape and Highveld',
    description: 'Mock topic run. Critical climate signal for plants that rely on municipal water.',
    country: 'ZA',
    city: 'Cape Town',
    coordinates: '-33.9249,18.4241',
    sourceUrl: 'https://dws.example/drought/cape-2026',
  },
  'WT-TOPIC-BR-POLITICS': {
    severity: 'high',
    title: 'Congressional probe into health procurement contracts',
    description: 'Mock topic run. Reputation signal for Brazilian manufacturers tied to public tenders.',
    country: 'BR',
    city: 'Sao Paulo',
    coordinates: '-23.5505,-46.6333',
    sourceUrl: 'https://news.example/br-procurement-probe',
  },
  'WT-TOPIC-NEPAL-FLOODING': {
    severity: 'critical',
    title: 'Monsoon floods cut the Koshi corridor',
    description: 'Mock topic run. Critical climate signal for Nepalese plants and hydropower sites.',
    country: 'NP',
    city: 'Kathmandu',
    coordinates: '27.7172,85.3240',
    sourceUrl: 'https://dhm.example/flood/koshi-2026',
  },
  'WT-TOPIC-EU-IMMIGRATION-LAW': {
    severity: 'high',
    title: 'EU labour-permit reform tightens plant staffing',
    description: 'Mock topic run. Regulation signal for manufacturers that rely on posted and seasonal labour.',
    country: 'BE',
    city: 'Brussels',
    coordinates: '50.8503,4.3517',
    sourceUrl: 'https://eur-lex.example/immigration-labour-2026',
  },
};

function cannedTopicFinding(topic: WatchTopic, now: Date): CannedTopicFinding {
  const canned = CANNED_TOPIC_FINDINGS[topic.uid];
  if (canned) {
    return canned;
  }
  return {
    severity: 'high',
    title: `Mock ${formatAgentType(topic.agentType)} topic finding`,
    description: `Inserted by Run now for topic ${topic.title} at ${now.toISOString()}.`,
    country: '',
    city: '',
    coordinates: '',
    sourceUrl: null,
  };
}

export function findingPayloadString(finding: WatchFinding, key: string): string {
  const value = finding.payloadJson?.[key];
  return typeof value === 'string' ? value.trim() : '';
}

export function runTopicNow(
  state: WatchTowerMockState,
  topicId: number,
  now = new Date(),
): WatchTowerMockState {
  const topic = topicById(state, topicId);
  if (!topic || topic.status === 'archived' || topic.paused) {
    return state;
  }
  const schedule = scheduleForTopic(state, topicId);
  if (!schedule) {
    return state;
  }
  const stamp = now.toISOString();
  const canned = cannedTopicFinding(topic, now);
  const runId = nextRunId(state);
  const findingId = nextFindingId(state);
  const run: WatchRun = {
    id: runId,
    scheduleId: schedule.id,
    scheduledAt: stamp,
    watchCompanyId: null,
    watchTopicId: topicId,
    watchTargetKind: 'topic',
    watchTargetId: topicId,
    type: topic.agentType,
    status: 'completed',
    costUsd: 0.18,
    durationMs: 52000,
    summary: {
      high_risk_count: canned.severity === 'high' || canned.severity === 'critical' ? 1 : 0,
      medium_risk_count: canned.severity === 'medium' ? 1 : 0,
      low_risk_count: 0,
    },
  };
  const finding: WatchFinding = {
    id: findingId,
    publicId: mockPublicId(now),
    agentRunId: runId,
    watchCompanyId: null,
    sourceKind: 'topic',
    watchTopicId: topicId,
    findingType: FINDING_TYPE_BY_AGENT[topic.agentType] || 'finding',
    severity: canned.severity,
    title: canned.title,
    description: canned.description,
    observedAt: stamp,
    confidenceScore: 0.78,
    sourceUrl: canned.sourceUrl,
    payloadJson: {
      mock: true,
      sourceKind: 'topic',
      topicUid: topic.uid,
      matchKinds: topic.matchKinds,
      country: canned.country,
      city: canned.city,
      coordinates: canned.coordinates,
    },
  };
  const hits = TOPIC_ELEVATED_SEVERITIES.has(canned.severity)
    ? matchWatchCompaniesForTopic(state, topic)
    : [];
  const links: WatchFindingAffected[] = [];
  hits.forEach((company) => {
    links.push({
      id: nextAffectedId(state, links),
      findingId,
      sourceWatchCompanyId: 0,
      affectedWatchCompanyId: company.id,
      relationKind: 'topic_match',
      watchProductId: company.products[0]?.id || null,
      flaggedBy: 'topic_match',
      status: 'linked',
      owner: null,
      notifiedAt: null,
      talkingPoint: `${company.name} matches ${topic.title}.`,
    });
  });

  return {
    ...state,
    runs: [...state.runs, run],
    findings: [...state.findings, finding],
    findingAffectedCompanies: [...state.findingAffectedCompanies, ...links],
    watchTopics: state.watchTopics.map((item) => (
      item.id === topicId ? { ...item, lastRunAt: stamp } : item
    )),
    schedules: state.schedules.map((item) => (
      item.watchTopicId === topicId
        ? {
          ...item,
          lastRunStatus: 'completed',
          nextRunAt: mockNextRunAt(item.hourOfDay, item.frequency, now),
        }
        : item
    )),
  };
}

function seedTopicExampleIssues(state: WatchTowerMockState): WatchTowerMockState {
  let next = state;
  seedWatchTopics().forEach((catalog, index) => {
    const topic = next.watchTopics.find((item) => item.uid === catalog.uid);
    if (!topic) {
      return;
    }
    if (next.findings.some((finding) => finding.watchTopicId === topic.id)) {
      return;
    }
    const stamp = new Date(Date.UTC(2026, 8, 2, 3 + index, 0, 0));
    next = runTopicNow(next, topic.id, stamp);
  });
  return next;
}

export function ensureWatchTopics(state: WatchTowerMockState): WatchTowerMockState {
  const catalog = seedWatchTopics();
  const current = Array.isArray(state.watchTopics) ? state.watchTopics : [];
  const haveUid = new Set(current.map((item) => item.uid));
  const missing = catalog.filter((item) => !haveUid.has(item.uid));
  let nextId = current.reduce((max, item) => Math.max(max, item.id), 0);
  const additions = missing.map((item) => {
    if (current.some((row) => row.id === item.id)) {
      nextId += 1;
      return { ...item, id: nextId };
    }
    nextId = Math.max(nextId, item.id);
    return item;
  });
  const watchTopics = [...current, ...additions];
  const withoutSchedule = watchTopics.filter((topic) => (
    !state.schedules.some((row) => row.watchTopicId === topic.id)
  ));
  const extraSchedules = seedTopicSchedules(withoutSchedule, nextScheduleId(state));
  return seedTopicExampleIssues({
    ...state,
    watchTopics,
    schedules: [...state.schedules, ...extraSchedules],
  });
}

export function setTopicPaused(
  state: WatchTowerMockState,
  topicId: number,
  paused: boolean,
): WatchTowerMockState {
  return {
    ...state,
    watchTopics: state.watchTopics.map((item) => (
      item.id === topicId ? { ...item, paused } : item
    )),
    schedules: state.schedules.map((item) => (
      item.watchTopicId === topicId ? { ...item, paused, nextRunAt: paused ? null : item.nextRunAt } : item
    )),
  };
}

export function setTopicStatus(
  state: WatchTowerMockState,
  topicId: number,
  status: WatchTopic['status'],
): WatchTowerMockState {
  const paused = status === 'archived';
  return {
    ...state,
    watchTopics: state.watchTopics.map((item) => (
      item.id === topicId ? { ...item, status, paused: paused || item.paused } : item
    )),
    schedules: state.schedules.map((item) => (
      item.watchTopicId === topicId
        ? { ...item, paused: paused || item.paused, nextRunAt: paused ? null : item.nextRunAt }
        : item
    )),
  };
}

export function registerTopicNamedMiss(
  state: WatchTowerMockState,
  topicId: number,
  missUid: string,
): WatchTowerMockState {
  const topic = topicById(state, topicId);
  if (!topic) {
    return state;
  }
  const miss = topic.namedMisses.find((item) => item.uid === missUid);
  if (!miss || miss.importedWatchCompanyId) {
    return state;
  }
  const companyId = nextWatchCompanyId(state);
  const draft = {
    ...createDraftWatchCompany(companyId),
    name: miss.name,
    uid: miss.uid,
    addressCountry: miss.addressCountry,
    addressCity: miss.addressCity,
    notes: miss.notes,
  };
  const latestFinding = findingsForTopic(state, topicId)[0];
  const withCompany: WatchTowerMockState = {
    ...state,
    watchCompanies: [...state.watchCompanies, draft],
    watchTopics: state.watchTopics.map((item) => (
      item.id === topicId
        ? {
          ...item,
          namedMisses: item.namedMisses.map((row) => (
            row.uid === missUid ? { ...row, importedWatchCompanyId: companyId } : row
          )),
        }
        : item
    )),
    findingAffectedCompanies: latestFinding
      ? [
        ...state.findingAffectedCompanies,
        {
          id: nextAffectedId(state),
          findingId: latestFinding.id,
          sourceWatchCompanyId: 0,
          affectedWatchCompanyId: companyId,
          relationKind: 'topic_match',
          watchProductId: null,
          flaggedBy: 'topic_match',
          status: 'linked',
          owner: null,
          notifiedAt: null,
          talkingPoint: `${miss.name} imported from a named miss on ${topic.title}.`,
        },
      ]
      : state.findingAffectedCompanies,
  };
  return enqueueCompanyInvestigation(withCompany, [companyId]);
}
